<script setup lang="ts">
import { ref, computed, watch, inject } from "vue";
import { useRouter } from "vue-router";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import DMSuiteIcon from "@/components/iconify/DMSuiteIcon.vue";
import { DASHBOARD_ICONS } from "@/config/dashboardIcons";

const props = defineProps<{
  open: boolean;
  service: any; // The selected service object
}>();

const emit = defineEmits<{
  (event: "update:open", value: boolean): void;
}>();

const comfortMode = inject<any>("comfortMode", ref("comfortable"));

const router = useRouter();
const activeTab = ref("all");

const tabs = [
  { id: "all", label: "All Tasks" },
  { id: "in_progress", label: "On-going" },
  { id: "needs_review", label: "Needs Review" },
  { id: "completed", label: "Closed" }
];

// Clean tasks inside the service
const serviceTasks = computed(() => {
  if (!props.service || !Array.isArray(props.service.tasks)) {
    return [];
  }
  return props.service.tasks.map((task: any) => {
    return {
      id: task.id,
      task_id: task.task_id || task.id,
      title: task.title || task.name || task.subject || "Untitled Task",
      status: task.status || "Open",
      progress: task.progress || task.progress_percentage || 0,
      due_date: task.due_date || task.dueDate || null,
      assigned_to: task.assigned_to || task.assignedUser?.name || "Unassigned",
      description: task.instructions || task.description || ""
    };
  });
});

const taskCounts = computed(() => {
  const counts = { completed: 0, in_progress: 0, pending: 0, needs_review: 0 };
  serviceTasks.value.forEach((t) => {
    const s = t.status.toLowerCase();
    if (s.includes("completed") || s.includes("done") || s.includes("closed")) {
      counts.completed++;
    } else if (s.includes("progress") || s.includes("ongoing")) {
      counts.in_progress++;
    } else if (s.includes("review") || s.includes("feedback") || s.includes("approval")) {
      counts.needs_review++;
    } else {
      counts.pending++;
    }
  });
  return counts;
});

const filteredTasks = computed(() => {
  const list = serviceTasks.value;
  if (activeTab.value === "all") return list;

  return list.filter((task) => {
    const s = task.status.toLowerCase();
    if (activeTab.value === "completed") {
      return s.includes("completed") || s.includes("done") || s.includes("closed");
    }
    if (activeTab.value === "in_progress") {
      return s.includes("progress") || s.includes("ongoing");
    }
    if (activeTab.value === "needs_review") {
      return s.includes("review") || s.includes("feedback") || s.includes("approval");
    }
    return false;
  });
});

function close() {
  emit("update:open", false);
}

function handleViewTask(task: any) {
  close();
  router.push({
    path: "/apps/task-dashboard",
    query: {
      taskId: String(task.task_id),
      focusTask: "true",
      source: "service-drawer"
    }
  });
}

function handleReviewTask(task: any) {
  close();
  router.push({
    path: "/apps/task-dashboard",
    query: {
      taskId: String(task.task_id),
      focusTask: "true",
      highlight: "true",
      source: "service-drawer"
    }
  });
}

function getTaskStatusClass(status: string) {
  const normalized = status.toLowerCase();
  if (normalized.includes("completed") || normalized.includes("done") || normalized.includes("closed")) {
    return "bg-emerald-50 text-emerald-700 border-emerald-100";
  }
  if (normalized.includes("progress") || normalized.includes("ongoing")) {
    return "bg-blue-50 text-blue-700 border-blue-100";
  }
  if (normalized.includes("review") || normalized.includes("feedback") || normalized.includes("approval")) {
    return "bg-amber-50 text-amber-700 border-amber-100";
  }
  return "bg-slate-50 text-slate-600 border-slate-100";
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "No due date";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Reset tab when active service changes
watch(() => props.service?.id, () => {
  activeTab.value = "all";
});
</script>

<template>
  <div
    v-if="open"
    class="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-sm transition duration-300"
  >
    <!-- Click outside to close -->
    <div
      class="absolute inset-0"
      @click="close"
    />
    
    <!-- Drawer Container -->
    <div class="relative w-full max-w-lg bg-white h-screen shadow-2xl flex flex-col z-10 transition-transform duration-300 animate-slide-in">
      <!-- Drawer Header -->
      <div
        class="border-b border-slate-100 flex items-center justify-between text-left"
        :class="comfortMode === 'comfortable' ? 'p-6 md:p-8' : 'p-6'"
      >
        <div class="flex items-center gap-3.5">
          <div
            class="flex items-center justify-center shrink-0"
            :class="[
              service?.iconWrapClass || 'bg-indigo-50 text-indigo-600',
              comfortMode === 'comfortable' ? 'h-13 w-13 rounded-2xl' : 'h-11 w-11 rounded-xl'
            ]"
          >
            <DMSuiteIcon
              v-if="typeof service?.icon === 'string'"
              :icon="service.icon"
              :size="comfortMode === 'comfortable' ? 26 : 22"
              :custom-class="service?.iconClass || 'text-indigo-600'"
            />
            <component
              :is="service?.icon"
              v-else
              :class="[
                service?.iconClass || 'text-indigo-600',
                comfortMode === 'comfortable' ? 'size-6.5' : 'size-5'
              ]"
            />
          </div>
          <div class="min-w-0">
            <h2
              :class="
                cn(
                  'font-black text-slate-900 truncate',
                  comfortMode === 'comfortable' ? 'text-xl md:text-2xl' : 'text-lg font-extrabold text-slate-800'
                )
              "
            >
              {{ service?.label || 'Service Details' }}
            </h2>
            <div class="flex items-center flex-wrap gap-2 mt-1">
              <span
                :class="
                  cn(
                    'font-semibold',
                    comfortMode === 'comfortable' ? 'text-sm text-slate-500' : 'text-xs text-slate-400 font-semibold'
                  )
                "
              >
                {{ service?.description }}
              </span>
              <Badge
                variant="outline"
                class="rounded-full font-bold whitespace-nowrap"
                :class="[
                  service?.badgeClass,
                  comfortMode === 'comfortable' ? 'px-2.5 py-0.5 text-xs' : 'px-2 py-0.2 text-[10px]'
                ]"
              >
                {{ service?.status }}
              </Badge>
            </div>
          </div>
        </div>
        <button
          class="h-8.5 w-8.5 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition"
          @click="close"
        >
          <DMSuiteIcon :icon="DASHBOARD_ICONS.close" :size="18" />
        </button>
      </div>

      <!-- Drawer Content -->
      <div
        class="flex-1 overflow-y-auto"
        :class="comfortMode === 'comfortable' ? 'p-6 md:p-8 space-y-7' : 'p-6 space-y-6'"
      >
        <!-- Summary Stats Chips -->
        <div class="grid grid-cols-4 gap-3">
          <div class="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
            <p
              :class="
                cn(
                  'font-black text-emerald-700',
                  comfortMode === 'comfortable' ? 'text-xl md:text-2xl' : 'text-lg'
                )
              "
            >
              {{ taskCounts.completed }}
            </p>
            <p
              :class="
                cn(
                  'font-bold uppercase',
                  comfortMode === 'comfortable' ? 'text-[11px] text-emerald-600' : 'text-[10px] text-emerald-500'
                )
              "
            >
              Closed
            </p>
          </div>
          <div class="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
            <p
              :class="
                cn(
                  'font-black text-blue-700',
                  comfortMode === 'comfortable' ? 'text-xl md:text-2xl' : 'text-lg'
                )
              "
            >
              {{ taskCounts.in_progress }}
            </p>
            <p
              :class="
                cn(
                  'font-bold uppercase',
                  comfortMode === 'comfortable' ? 'text-[11px] text-blue-600' : 'text-[10px] text-blue-500'
                )
              "
            >
              Active
            </p>
          </div>
          <div class="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
            <p
              :class="
                cn(
                  'font-black text-amber-700',
                  comfortMode === 'comfortable' ? 'text-xl md:text-2xl' : 'text-lg'
                )
              "
            >
              {{ taskCounts.needs_review }}
            </p>
            <p
              :class="
                cn(
                  'font-bold uppercase',
                  comfortMode === 'comfortable' ? 'text-[11px] text-amber-600' : 'text-[10px] text-amber-500'
                )
              "
            >
              Review
            </p>
          </div>
          <div class="bg-slate-50 border border-slate-100 rounded-xl p-3 text-center">
            <p
              :class="
                cn(
                  'font-black text-slate-700',
                  comfortMode === 'comfortable' ? 'text-xl md:text-2xl' : 'text-lg'
                )
              "
            >
              {{ taskCounts.pending }}
            </p>
            <p
              :class="
                cn(
                  'font-bold uppercase',
                  comfortMode === 'comfortable' ? 'text-[11px] text-slate-600' : 'text-[10px] text-slate-500'
                )
              "
            >
              Open Ticket
            </p>
          </div>
        </div>

        <!-- Service Progress Bar Section -->
        <div class="space-y-2 text-left">
          <div
            :class="
              cn(
                'flex items-center justify-between font-bold text-slate-700',
                comfortMode === 'comfortable' ? 'text-base text-slate-800' : 'text-sm'
              )
            "
          >
            <span>Overall Progress</span>
            <span>{{ service?.progress }}%</span>
          </div>
          <div
            class="w-full bg-slate-100 rounded-full overflow-hidden"
            :class="comfortMode === 'comfortable' ? 'h-3.5' : 'h-2.5'"
          >
            <div
              class="h-full rounded-full transition-all duration-500"
              :class="service?.progressClass || 'bg-indigo-600'"
              :style="{ width: `${service?.progress}%` }"
            />
          </div>
        </div>

        <!-- Task Filter Tabs -->
        <div class="flex border-b border-slate-100">
          <button
            v-for="t in tabs"
            :key="t.id"
            class="flex-1 text-center transition duration-200"
            :class="[
              activeTab === t.id 
                ? 'border-indigo-600 text-indigo-600 border-b-2 font-black' 
                : 'border-transparent text-slate-500 hover:text-slate-700 font-bold',
              comfortMode !== 'comfortable' ? 'pb-3.5 text-base md:text-lg' : 'pb-3 text-sm'
            ]"
            @click="activeTab = t.id"
          >
            {{ t.label }}
          </button>
        </div>

        <!-- Tasks List -->
        <div class="space-y-4">
          <div
            v-if="filteredTasks.length === 0"
            class="text-center p-8 border border-dashed border-slate-200 bg-slate-50/50 rounded-2xl"
          >
            <p class="text-sm text-slate-400 font-semibold">
              No tasks found for this status.
            </p>
          </div>
          <div
            v-for="task in filteredTasks"
            :key="task.id"
            class="bg-white border border-slate-200/80 rounded-2xl shadow-sm hover:shadow-md transition duration-200 text-left"
            :class="[
              comfortMode === 'comfortable' ? 'p-5 md:p-6 space-y-4' : 'p-4 space-y-3'
            ]"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="space-y-1">
                <h4
                  :class="
                    cn(
                      'font-black text-slate-900 leading-snug',
                      comfortMode === 'comfortable' ? 'text-base md:text-lg' : 'text-sm sm:text-base font-extrabold'
                    )
                  "
                >
                  {{ task.title }}
                </h4>
                <p
                  :class="
                    cn(
                      'font-semibold',
                      comfortMode === 'comfortable' ? 'text-sm text-slate-500' : 'text-xs text-slate-400'
                    )
                  "
                >
                  Due: {{ formatDate(task.due_date) }} • Assignee: {{ task.assigned_to }}
                </p>
              </div>
              <Badge
                variant="outline"
                class="rounded-full font-bold whitespace-nowrap"
                :class="[
                  getTaskStatusClass(task.status),
                  comfortMode === 'comfortable' ? 'px-2.5 py-0.5 text-xs' : 'px-2 py-0.2 text-[10px]'
                ]"
              >
                {{ task.status }}
              </Badge>
            </div>
            
            <p
              v-if="task.description"
              :class="
                cn(
                  'leading-relaxed font-medium max-h-20 overflow-y-auto',
                  comfortMode === 'comfortable' ? 'text-sm text-slate-600' : 'text-xs text-slate-500 font-medium'
                )
              "
            >
              {{ task.description }}
            </p>

            <!-- Task Progress Bar -->
            <div class="space-y-2">
              <div
                :class="
                  cn(
                    'flex items-center justify-between font-bold uppercase tracking-wider',
                    comfortMode === 'comfortable' ? 'text-xs text-slate-500' : 'text-[10px] text-slate-400'
                  )
                "
              >
                <span>Task Completion</span>
                <span>{{ task.progress }}%</span>
              </div>
              <div
                class="w-full bg-slate-100 rounded-full overflow-hidden"
                :class="comfortMode === 'comfortable' ? 'h-2.5' : 'h-1.5'"
              >
                <div
                  class="h-full bg-indigo-600 rounded-full transition-all duration-300"
                  :style="{ width: `${task.progress}%` }"
                />
              </div>
            </div>

            <!-- Actions inside Task Card -->
            <div class="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button
                size="sm"
                variant="ghost"
                :class="
                  cn(
                    'font-bold text-slate-600',
                    comfortMode === 'comfortable' ? 'h-10 px-4 text-sm' : 'h-8 text-xs font-bold'
                  )
                "
                @click="handleViewTask(task)"
              >
                View Task
              </Button>
              <Button
                size="sm"
                :class="
                  cn(
                    'bg-indigo-600 text-white font-bold',
                    comfortMode === 'comfortable' ? 'h-10 px-5 text-sm rounded-xl' : 'h-8 px-4 text-xs'
                  )
                "
                @click="handleReviewTask(task)"
              >
                Review
              </Button>
            </div>
          </div>
        </div>
      </div>

      <!-- Drawer Footer Actions -->
      <div
        class="border-t border-slate-100 bg-slate-50/50 flex gap-4"
        :class="comfortMode === 'comfortable' ? 'p-6 md:p-8' : 'p-6'"
      >
        <Button
          variant="outline"
          :class="
            cn(
              'flex-1 font-bold border-slate-200 text-slate-600',
              comfortMode === 'comfortable' ? 'min-h-12 text-sm md:text-base rounded-xl' : 'text-xs py-3 rounded-xl'
            )
          "
          @click="close"
        >
          Close Details
        </Button>
        <Button
          :class="
            cn(
              'flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold flex items-center justify-center gap-1.5',
              comfortMode === 'comfortable' ? 'min-h-12 text-sm md:text-base rounded-xl' : 'text-xs py-3 rounded-xl'
            )
          "
          @click="router.push('/apps/task-dashboard')"
        >
          Open Task Dashboard
          <DMSuiteIcon :icon="DASHBOARD_ICONS.externalLink" :size="16" />
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}
.animate-slide-in {
  animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
</style>
